export const CRITTER_PROJECT_MANIFEST_FILENAME = "project.json";

export type CritterProjectAssetManifestEntryV1 = {
  path: string;
  role: "mesh" | "texture" | "sidecar" | "asset";
  referencedBy: string[];
  byteLength: number;
  sha256?: string;
};

export type CritterProjectIntegrityPolicyV1 = "sha256-required" | "best-effort";

export type CritterProjectIntegrityReasonV1 =
  | "sha256-unavailable"
  | "user-override"
  | "asset-read-error"
  | "legacy-missing-integrity";

export type CritterProjectIntegrityV1 = {
  policy: CritterProjectIntegrityPolicyV1;
  complete: boolean;
  reason?: CritterProjectIntegrityReasonV1;
  details?: {
    originalReason?: CritterProjectIntegrityReasonV1;
  };
};

export type CritterProjectManifestV1 = {
  type: "critterapp-project-manifest";
  schemaVersion: 1;
  appVersion: string;
  projectName: string;
  createdAtIso: string;
  modifiedAtIso: string;
  sourceOfTruth: "critter-project";
  files: {
    projectSnapshot: string;
    model: string;
    sceneConfig: string;
    assetsRoot: string;
  };
  integrity: CritterProjectIntegrityV1;
  assets: CritterProjectAssetManifestEntryV1[];
};

const asString = (value: unknown, fallback: string): string =>
  typeof value === "string" && value.trim() ? value : fallback;

const asAssetRole = (value: unknown): CritterProjectAssetManifestEntryV1["role"] => {
  if (value === "mesh" || value === "texture" || value === "sidecar" || value === "asset") {
    return value;
  }
  return "asset";
};

const asIntegrityReason = (value: unknown): CritterProjectIntegrityReasonV1 | undefined => {
  if (
    value === "sha256-unavailable" ||
    value === "user-override" ||
    value === "asset-read-error" ||
    value === "legacy-missing-integrity"
  ) {
    return value;
  }
  return undefined;
};

const inferLegacyIntegrity = (
  assets: CritterProjectAssetManifestEntryV1[]
): CritterProjectIntegrityV1 => {
  const complete = assets.every((asset) => Boolean(asset.sha256));
  return complete
    ? { policy: "sha256-required", complete: true }
    : { policy: "best-effort", complete: false, reason: "legacy-missing-integrity" };
};

const migrateIntegrity = (
  value: unknown,
  assets: CritterProjectAssetManifestEntryV1[]
): CritterProjectIntegrityV1 | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return inferLegacyIntegrity(assets);
  }

  const obj = value as Record<string, unknown>;
  const policy = obj.policy;
  if (policy !== "sha256-required" && policy !== "best-effort") return null;
  const complete = obj.complete === true;
  const reason = asIntegrityReason(obj.reason);
  const detailsRaw =
    obj.details && typeof obj.details === "object" && !Array.isArray(obj.details)
      ? (obj.details as Record<string, unknown>)
      : null;
  const originalReason = asIntegrityReason(detailsRaw?.originalReason);
  const details = originalReason ? { originalReason } : undefined;

  if (complete) return { policy, complete: true, ...(details ? { details } : {}) };
  return {
    policy,
    complete: false,
    ...(reason ? { reason } : {}),
    ...(details ? { details } : {}),
  };
};

export function buildProjectManifest(args: {
  appVersion: string;
  projectName: string;
  createdAtIso?: string;
  projectSnapshotPath: string;
  modelXmlPath: string;
  sceneConfigPath: string;
  assetsRoot?: string;
  assets?: CritterProjectAssetManifestEntryV1[];
  integrity?: CritterProjectIntegrityV1;
}): CritterProjectManifestV1 {
  const now = new Date().toISOString();
  const assets = Array.isArray(args.assets) ? args.assets : [];
  return {
    type: "critterapp-project-manifest",
    schemaVersion: 1,
    appVersion: String(args.appVersion || "0.0.0"),
    projectName: String(args.projectName || "Untitled Scene"),
    createdAtIso: args.createdAtIso || now,
    modifiedAtIso: now,
    sourceOfTruth: "critter-project",
    files: {
      projectSnapshot: args.projectSnapshotPath || "critter_project.json",
      model: args.modelXmlPath || "model.xml",
      sceneConfig: args.sceneConfigPath || "scene_config.json",
      assetsRoot: args.assetsRoot || "assets/",
    },
    integrity: args.integrity || inferLegacyIntegrity(assets),
    assets,
  };
}

export function migrateProjectManifest(raw: unknown): CritterProjectManifestV1 | null {
  if (!raw || typeof raw !== "object") return null;
  const obj = raw as Record<string, unknown>;
  if (obj.type !== "critterapp-project-manifest") return null;
  const schemaVersion = Number(obj.schemaVersion) || 0;
  if (schemaVersion !== 1) return null;

  const files =
    obj.files && typeof obj.files === "object" ? (obj.files as Record<string, unknown>) : {};
  const assets = Array.isArray(obj.assets)
    ? obj.assets
        .map((asset): CritterProjectAssetManifestEntryV1 | null => {
          if (!asset || typeof asset !== "object") return null;
          const entry = asset as Record<string, unknown>;
          const path = asString(entry.path, "");
          if (!path) return null;
          return {
            path,
            role: asAssetRole(entry.role),
            referencedBy: Array.isArray(entry.referencedBy)
              ? entry.referencedBy.map((value) => String(value || "")).filter(Boolean)
              : [],
            byteLength: Number.isFinite(Number(entry.byteLength)) ? Number(entry.byteLength) : 0,
            sha256:
              typeof entry.sha256 === "string" && entry.sha256.trim() ? entry.sha256 : undefined,
          };
        })
        .filter((asset): asset is CritterProjectAssetManifestEntryV1 => Boolean(asset))
    : [];
  const integrity = migrateIntegrity(obj.integrity, assets);
  if (!integrity) return null;

  return {
    type: "critterapp-project-manifest",
    schemaVersion: 1,
    appVersion: asString(obj.appVersion, "unknown"),
    projectName: asString(obj.projectName, "Untitled Scene"),
    createdAtIso: asString(obj.createdAtIso, new Date(0).toISOString()),
    modifiedAtIso: asString(
      obj.modifiedAtIso,
      asString(obj.createdAtIso, new Date(0).toISOString())
    ),
    sourceOfTruth: "critter-project",
    files: {
      projectSnapshot: asString(files.projectSnapshot, "critter_project.json"),
      model: asString(files.model, "model.xml"),
      sceneConfig: asString(files.sceneConfig, "scene_config.json"),
      assetsRoot: asString(files.assetsRoot, "assets/"),
    },
    integrity,
    assets,
  };
}
